import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ChevronLeft, ChevronRight, Pause, Play } from 'lucide-react';
import ShowcaseStage from './ShowcaseStage.jsx';
import { FEATURE_REEL, FEATURE_REEL_IDS } from './showcaseTimeline.js';
import { useInView, useMediaQuery, useReducedMotion, useShowcaseRuntime } from './useShowcaseRuntime.js';
import { formatShowcaseCopy } from './showcaseCopyFormat.js';

const LAST_FRAME = FEATURE_REEL.durationInFrames - 1;

const ReelCard = ({
  item,
  index,
  count,
  runtime,
  inputProps,
  isActive,
  isPlaying,
  isLive,
  onLive,
  playerRef,
  onToggle,
  onHover,
  onFrame,
  onPlayingChange,
  onEnded,
  progressRef,
  copy
}) => {
  const label = formatShowcaseCopy(isPlaying ? copy.pauseLabel : copy.playLabel, { title: item.title });
  const Icon = isPlaying ? Pause : Play;
  return (
    <li
      className={`stk-reel-card${isActive ? ' is-active' : ''}${isPlaying ? ' is-playing' : ''}`}
      data-reel-index={index}
      onPointerEnter={(event) => { if (event.pointerType === 'mouse') onHover(index); }}
    >
      <div className="stk-reel-frame">
        <span className="stk-reel-progress" aria-hidden="true">
          <span ref={progressRef} />
        </span>
        <ShowcaseStage
          ref={playerRef}
          runtime={runtime}
          compositionId={item.id}
          inputProps={inputProps}
          initialFrame={FEATURE_REEL.hold}
          onFrame={onFrame}
          onPlayingChange={onPlayingChange}
          onEnded={onEnded}
          onLive={onLive}
          variant="reel"
        />
        <button type="button" className="stk-reel-toggle" onClick={() => onToggle(index)} aria-label={label} disabled={!isLive}>
          <span className="stk-reel-toggle-icon"><Icon size={18} aria-hidden="true" /></span>
        </button>
      </div>
      <div className="stk-reel-text">
        <span className="stk-reel-count" aria-hidden="true">{String(index + 1).padStart(2, '0')} / {String(count).padStart(2, '0')}</span>
        <h3>{item.title}</h3>
        <p>{item.caption}</p>
      </div>
    </li>
  );
};

const HomeShowcaseReels = ({ copy, language = 'en', localeTag = 'en-US', theme = 'true-dark' }) => {
  const sectionRef = useRef(null);
  const listRef = useRef(null);
  const playerRefs = useRef([]);
  const progressRefs = useRef([]);
  const userPausedRef = useRef(new Set());
  // The reel the visitor started with its play button. It plays even when
  // reduced motion keeps every other reel from starting on its own.
  const requestedRef = useRef(-1);
  // Set while the list scrolls itself to a card, so the visibility observer
  // does not hand the lead back to the card being scrolled away from.
  const scrollTargetRef = useRef(null);

  const runtime = useShowcaseRuntime(sectionRef, { language });
  const reducedMotion = useReducedMotion();
  const hoverCapable = useMediaQuery('(hover: hover) and (pointer: fine)');
  const sectionInView = useInView(listRef, { threshold: 0.35 });

  const items = useMemo(() => {
    const byId = new Map((copy?.items || []).map((item) => [item.id, item]));
    return FEATURE_REEL_IDS.map((id) => byId.get(id)).filter(Boolean);
  }, [copy]);

  const inputPropsList = useMemo(
    () => items.map((item) => ({ copy: item, language, localeTag, theme })),
    [items, language, localeTag, theme]
  );

  const [activeIndex, setActiveIndex] = useState(0);
  const [playingIndex, setPlayingIndex] = useState(-1);
  const [liveCards, setLiveCards] = useState(() => new Set());
  const markLive = useCallback((index) => {
    setLiveCards((current) => (current.has(index) ? current : new Set(current).add(index)));
  }, []);

  // Narrow screens: the reel most visible inside the horizontal scroller leads.
  useEffect(() => {
    const list = listRef.current;
    if (!list || hoverCapable || typeof window === 'undefined' || !('IntersectionObserver' in window)) return undefined;
    const observer = new window.IntersectionObserver((entries) => {
      if (scrollTargetRef.current !== null) return;
      const best = entries
        .filter((entry) => entry.isIntersecting && entry.intersectionRatio >= 0.6)
        .sort((a, b) => b.intersectionRatio - a.intersectionRatio)[0];
      if (best) setActiveIndex(Number(best.target.dataset.reelIndex) || 0);
    }, { root: list, threshold: [0.6, 0.85, 1] });
    Array.from(list.children).forEach((child) => observer.observe(child));
    let settleTimer = 0;
    const release = () => { scrollTargetRef.current = null; };
    // scrollend where supported; a short quiet period elsewhere (Safari).
    const handleScroll = () => {
      window.clearTimeout(settleTimer);
      settleTimer = window.setTimeout(release, 160);
    };
    list.addEventListener('scrollend', release);
    list.addEventListener('scroll', handleScroll, { passive: true });
    return () => {
      observer.disconnect();
      window.clearTimeout(settleTimer);
      list.removeEventListener('scrollend', release);
      list.removeEventListener('scroll', handleScroll);
    };
  }, [hoverCapable, items.length]);

  // Scrolls the strip to a card only when it is actually out of view.
  const revealCard = useCallback((index, behavior) => {
    const list = listRef.current;
    const card = list?.children[index];
    if (!list || !card || list.scrollWidth <= list.clientWidth + 1) return;
    const listRect = list.getBoundingClientRect();
    const cardRect = card.getBoundingClientRect();
    if (cardRect.left >= listRect.left - 1 && cardRect.right <= listRect.right + 1) return;
    scrollTargetRef.current = index;
    list.scrollTo({ left: card.offsetLeft - list.offsetLeft, behavior });
  }, []);

  // Only the active reel plays; the others rest on their finished frame.
  useEffect(() => {
    if (!runtime) return;
    if (requestedRef.current !== activeIndex) requestedRef.current = -1;
    playerRefs.current.forEach((player, index) => {
      if (!player) return;
      const shouldPlay = index === activeIndex
        && sectionInView
        && (!reducedMotion || index === requestedRef.current)
        && !userPausedRef.current.has(index);
      if (shouldPlay) {
        if (!player.isPlaying()) {
          if (player.getCurrentFrame() >= FEATURE_REEL.hold) player.seekTo(0);
          player.play();
        }
      } else if (player.isPlaying()) {
        player.pause();
        if (index !== activeIndex) player.seekTo(FEATURE_REEL.hold);
      }
    });
  }, [activeIndex, reducedMotion, runtime, sectionInView]);

  const handleFrame = useCallback((index, frame) => {
    progressRefs.current[index]?.style.setProperty('--stk-progress', (frame / LAST_FRAME).toFixed(3));
  }, []);

  const handlePlayingChange = useCallback((index, isPlaying) => {
    setPlayingIndex((current) => {
      if (isPlaying) return index;
      return current === index ? -1 : current;
    });
  }, []);

  // A finished reel rests on its finished frame (the last frames fade to
  // empty for the loop) and hands over to the next one, like stories.
  const handleEnded = useCallback((index) => {
    playerRefs.current[index]?.seekTo(FEATURE_REEL.hold);
    if (requestedRef.current === index) requestedRef.current = -1;
    if (reducedMotion) return;
    const next = (index + 1) % items.length;
    revealCard(next, 'smooth');
    playerRefs.current[next]?.seekTo(0);
    setActiveIndex(next);
  }, [items.length, reducedMotion, revealCard]);

  const handleHover = useCallback((index) => {
    if (!hoverCapable || reducedMotion) return;
    userPausedRef.current.delete(index);
    setActiveIndex(index);
  }, [hoverCapable, reducedMotion]);

  const handleToggle = (index) => {
    const player = playerRefs.current[index];
    if (!player) return;
    if (player.isPlaying()) {
      player.pause();
      userPausedRef.current.add(index);
      if (requestedRef.current === index) requestedRef.current = -1;
      return;
    }
    userPausedRef.current.delete(index);
    requestedRef.current = index;
    if (index !== activeIndex) setActiveIndex(index);
    // A reel resting on its finished frame starts over.
    if (player.getCurrentFrame() >= FEATURE_REEL.hold) player.seekTo(0);
    player.play();
  };

  const scrollByCard = (direction) => {
    const next = Math.min(items.length - 1, Math.max(0, activeIndex + direction));
    if (next === activeIndex) return;
    revealCard(next, reducedMotion ? 'auto' : 'smooth');
    userPausedRef.current.delete(next);
    setActiveIndex(next);
  };
  const atStart = activeIndex === 0;
  const atEnd = activeIndex === items.length - 1;

  if (!copy || !items.length) return null;

  return (
    <section
      id="guest-showcase-reels"
      ref={sectionRef}
      className="premium-section stk-showcase stk-reels"
      aria-labelledby="guest-showcase-reels-title"
      lang={language}
    >
      <div className="premium-shell">
        <div className="stk-reels-head" data-home-reveal>
          <div className="premium-section-intro">
            <p className="premium-eyebrow">{copy.eyebrow}</p>
            <h2 id="guest-showcase-reels-title">{copy.title}</h2>
            <p className="premium-section-description">{copy.description}</p>
          </div>
          <div className="stk-reels-nav">
            {/* aria-disabled rather than disabled: focus stays on the button at
                either end instead of being dropped. */}
            <button type="button" className="stk-icon-button stk-icon-button--round" onClick={() => scrollByCard(-1)} aria-disabled={atStart} aria-label={copy.previous}>
              <ChevronLeft size={18} aria-hidden="true" />
            </button>
            <button type="button" className="stk-icon-button stk-icon-button--round" onClick={() => scrollByCard(1)} aria-disabled={atEnd} aria-label={copy.next}>
              <ChevronRight size={18} aria-hidden="true" />
            </button>
          </div>
        </div>

        <ul className="stk-reel-list" ref={listRef} aria-label={copy.listLabel}>
          {items.map((item, index) => (
            <ReelCard
              key={item.id}
              item={item}
              index={index}
              count={items.length}
              runtime={runtime}
              inputProps={inputPropsList[index]}
              isActive={index === activeIndex}
              isPlaying={index === playingIndex}
              isLive={liveCards.has(index)}
              onLive={() => markLive(index)}
              playerRef={(node) => { playerRefs.current[index] = node; }}
              progressRef={(node) => { progressRefs.current[index] = node; }}
              onToggle={handleToggle}
              onHover={handleHover}
              onFrame={(frame) => handleFrame(index, frame)}
              onPlayingChange={(isPlaying) => handlePlayingChange(index, isPlaying)}
              onEnded={() => handleEnded(index)}
              copy={copy}
            />
          ))}
        </ul>
      </div>
    </section>
  );
};

export default HomeShowcaseReels;
